import { NavLink } from 'react-router-dom';
import {
  Home,
  FolderKanban,
  Filter,
  Settings,
  MessageSquare,
  Star,
  BookOpen,
  Target,
  FileStack,
  Lightbulb,
} from 'lucide-react';
import { useProjectFavorites } from '../../hooks/useProjectFavorites';
import OrgSwitcher from './OrgSwitcher';
import BugReportWidget from '../BugReportWidget';

const navigation = [
  { name: '마이 홈', href: '/', icon: Home },
  { name: '목표', href: '/goals', icon: Target },
  { name: '아이디어', href: '/ideas', icon: Lightbulb },
  { name: '프로젝트', href: '/projects', icon: FolderKanban },
  { name: '채팅', href: '/chat', icon: MessageSquare },
  { name: '필터', href: '/filters', icon: Filter },
  { name: '문서함', href: '/documents', icon: FileStack },
  { name: '용어사전', href: '/glossary', icon: BookOpen },
];

const bottomNav = [{ name: '설정', href: '/settings', icon: Settings }];

export default function Sidebar() {
  const { data: favoriteProjects } = useProjectFavorites();

  return (
    <aside className="w-56 bg-white border-r border-gray-200 flex flex-col shrink-0">
      {/* Organization switcher — 우측 채팅 헤더 h-14와 정렬 */}
      <div className="h-14 border-b border-gray-200 flex items-center shrink-0">
        <OrgSwitcher />
      </div>

      {/* Main navigation */}
      <nav className="flex-1 px-3 py-4 overflow-y-auto">
        <p className="px-3 mb-2 text-sm font-semibold text-gray-400 uppercase tracking-wider">
          메뉴
        </p>
        {navigation.map((item) => (
          <NavLink
            key={item.href}
            to={item.href}
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-primary-50 text-primary-700'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              }`
            }
          >
            <item.icon className="size-4 shrink-0" />
            {item.name}
          </NavLink>
        ))}

        {/* 즐겨찾기 섹션 [REQ-SB-009] */}
        <div className="border-t border-gray-200 my-5" />

        <p className="px-3 mb-2 text-sm font-semibold text-gray-400 uppercase tracking-wider">
          즐겨찾기
        </p>

        {favoriteProjects && favoriteProjects.length > 0 ? (
          <div className="space-y-1">
            {favoriteProjects.map((project) => (
              <NavLink
                key={project.id}
                to={`/projects/${project.projectKey}`}
                state={{ projectId: project.id }}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-3 py-1 rounded-lg text-sm transition-colors ${
                    isActive
                      ? 'bg-primary-50 text-primary-700 font-medium'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`
                }
              >
                <Star className="size-4 shrink-0 text-yellow-500 fill-yellow-500" />
                <span className="inline-flex items-center justify-center size-5 rounded bg-primary-100 text-primary-700 text-[11px] font-bold shrink-0">
                  {project.projectKey.slice(0, 2)}
                </span>
                <span className="truncate">{project.name}</span>
              </NavLink>
            ))}
          </div>
        ) : (
          <div className="px-3 py-2 text-sm text-gray-400">
            즐겨찾기한 프로젝트가 없습니다.
          </div>
        )}
      </nav>

      {/* Bottom navigation */}
      <div className="border-t border-gray-200 px-3 h-14 flex items-center shrink-0">
        <div className="grid grid-cols-2 gap-1 w-full">
          {bottomNav.map((item) => (
            <NavLink
              key={item.href}
              to={item.href}
              className={({ isActive }) =>
                `flex items-center gap-1.5 px-2 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`
              }
            >
              <item.icon className="size-3.5 shrink-0" />
              {item.name}
            </NavLink>
          ))}
          <BugReportWidget />
        </div>
      </div>
    </aside>
  );
}
